import type { Page } from '@unifyapps/network/generated/models/page';
import type { ResolvedUIFilter } from '@unifyapps/network/generated/models/resolvedUIFilter';
import type { SearchEntitiesDTO } from '@unifyapps/network/generated/models/searchEntitiesDTO';
import { UA_ASSETS_PATH } from '@unifyapps/network/utils/PublicEnv/assetsCdn';
import type { SecurityProperties } from '@unifyapps/network/utils/securityHeaders';
import type { JSONSchema7 } from 'json-schema';
import type { ReactNode } from 'react';
import type { VisibilityPayloadType } from '../block';
import type { ValueOrCondition } from '../conditionalValueFilters';
import type { ConditionEvaluationObject } from '../conditionEvaluationObject';
import type { DeviceDetails } from '../deviceVariant';
import type { App } from '../embeds';
import type { Event } from '../event';
import type { FileVisibility } from '../fileVisibility';
import type { JavaScriptFunction } from '../javascriptFunction';
import type { JavaScriptVariable } from '../javascriptVariable';
import type { InterfacePageEntityComponentType } from '../page';
import type { Entity } from '../page/entity';
import type { BlockThemeEnum, StyleObject } from '../styleObject';
import type { TypographyColors, TypographyVariants, TypographyWeights } from '../typography';
import type { ThemeComponentsType } from './types';

export enum NavigationResolution {
  XL = 'XL',
  LG = 'LG',
  MD = 'MD',
  SM = 'SM',
}

export type MQTTTopicType = {
  id: string;
  topic: string;
  eventSchema?: object;
};

export type CustomEventType = {
  id: string;
  eventName: string;
  eventSchema?: JSONSchema7;
};

export enum ApplicationType {
  WEB = 'WEB',
  MOBILE = 'MOBILE',
  COPILOT = 'COPILOT',
  DISPLAY = 'DISPLAY',
}

/**
 * How an interface is authored — deliberately decoupled from `ApplicationType` so
 * the AI code builder is a build-mode of a normal app (today only WEB), not its
 * own app type. `config` (the default when omitted) = the no-code/config builder;
 * `code` = the AI code builder, whose source of truth is an agent-platform
 * session (see `chatSessionId`).
 */
export const BuilderMode = {
  Code: 'code',
  Config: 'config',
} as const;

export type BuilderModeType = (typeof BuilderMode)[keyof typeof BuilderMode];

export enum ColorScheme {
  LIGHT = 'light',
  DARK = 'dark',
  SYSTEM = 'system',
}

export type BrandAssetsType = {
  android?: {
    foregroundLogo?: string;
    monochromeLogo?: string;
    backgroundType?: 'Color' | 'Image';
    backgroundColor?: string;
    backgroundImage?: string;
  };
  ios?: {
    lightModeLogo?: string;
    darkModeLogo?: string;
    tintedLogo?: string;
  };
};

export const DEFAULT_COLOR_SCHEME = ColorScheme.LIGHT;

export type ConditionalTheme = {
  themeId: string;
  condition: ConditionEvaluationObject | undefined;
};

/**
 * Filter item for conditional theme selection
 * propertyValue is the themeId to use when conditions are met
 */
export type ThemeConditionalFilter = {
  propertyValue: string; // themeId
  conditions: ConditionEvaluationObject | undefined;
};

/**
 * Conditional value filter for theme selection
 * Used when multiple themes are configured with conditions
 */
export type ThemeConditionalValueFilter = {
  conditionalValueFilters: ThemeConditionalFilter[];
  defaultValue: string; // default theme id when no conditions match
};

export type Metadata = {
  logo: {
    favicon?: string;
    small: string;
    large: string;
  };
  icon?: string;
  locale?: string;
  supportedLocales?: string[];
  advanced?: {
    location?: {
      enable?: boolean;
      liveTracking?: boolean;
      liveTrackingMode?: 'manual' | 'automatic';
      liveTrackingIntervalMs?: number;
      liveTrackingNotificationMessage?: string;
    };
    fileManagement?: {
      /**
       * App-wide fallback for blocks that upload files and have not set their own
       * visibility. A block that does set one always wins — see
       * useDefaultFileVisibility for where the two are combined.
       */
      defaultFileVisibility?: FileVisibility;
    };
  };
  _counter?: {
    _pageCount?: number;
    _moduleCount?: number;
    _templateComponentCount?: number;
    _variableCounter?: number;
    _dataSourceCounter?: number;
    _functionCounter?: number;
    _mqttTopicCounter?: number;
    _customEventCounter?: number;
  };
  splashScreen?: SplashScreenType;
};

export type InterfacePermissions = Record<string, string[]>;

export type PagePermissions = Record<string, InterfacePermissions>;

interface BaseNavItem {
  id: string;
  name: string;
  permissions?: InterfacePermissions;
  breakpointVisibility?: NavigationResolution[];
  showNestedDetails?: boolean;
}

export interface UrlNavItem extends BaseNavItem {
  type: 'URL';
  openInNewTab?: boolean;
  url: string;
  icon: string;
  activeIcon?: string;
}
export interface ButtonNavItem extends BaseNavItem {
  type: 'BUTTON';
  onClick?: (args: { onOpen: () => void; onClose: () => void }) => void;
  icon: string;
  interactions?: {
    events?: Event[];
  };
}

export type NavItemConfig = {
  type: 'label' | 'BUTTON' | 'COLOR_SCHEME_SWITCHER' | 'LANGUAGE_SWITCHER';
  id: string;
  title: string;
  url: string | false;
  selected: boolean;
  className: string;
  startDecoratorNode: ReactNode | null;
  titleClassName: string;
  events?: Event[];
};
export interface ThemeNavItem extends BaseNavItem {
  type: 'COLOR_SCHEME_SWITCHER';
}
export interface LanguageNavItem extends BaseNavItem {
  type: 'LANGUAGE_SWITCHER';
}

export interface PageNavItem extends BaseNavItem {
  type: 'PAGE';
  pageId: string;
  slug: string;
  path?: string;
  pageParams?: { key?: string; value?: unknown }[];
  pageInputs?: Record<string, unknown>;
  icon: string;
  activeIcon?: string;
}

export interface ModuleNavItem extends BaseNavItem {
  type: 'MODULE';
  moduleId: string;
  slug: string;
  icon: string;
  activeIcon?: string;
  pageInputs?: Record<string, unknown>;
}

export interface GroupNavItem extends BaseNavItem {
  type: 'GROUP';
  items: NavItem[];
  icon: string;
  activeIcon?: string;
}

export interface UserMenuNavItem extends BaseNavItem {
  type: 'USER_MENU';
  profilePage?: { pageId?: string; pageInputs?: Record<string, unknown> };
  menuItems?: (
    | UrlNavItem
    | PageNavItem
    | ButtonNavItem
    | LanguageSwitcherNavItem
    | ColorSchemeSwitcherNavItem
  )[];
}

export interface ActionNavItem extends BaseNavItem {
  type: 'ACTION';
  action?: 'showNotifications' | 'showHelp'; // can add more actions as needed
  onClick: (args: { onOpen: () => void; onClose: () => void }) => void;
  icon: string;
  activeIcon?: string;
}

export interface LanguageSwitcherNavItem extends BaseNavItem {
  type: 'LANGUAGE_SWITCHER';
}

export interface ColorSchemeSwitcherNavItem extends BaseNavItem {
  type: 'COLOR_SCHEME_SWITCHER';
}

export type NavItem =
  | UrlNavItem
  | PageNavItem
  | ModuleNavItem
  | GroupNavItem
  | UserMenuNavItem
  | ActionNavItem
  | ColorSchemeSwitcherNavItem
  | LanguageNavItem
  | ButtonNavItem;

export type NavItems = NavItem[];

export type NavItemsMap = Map<string, NavItem>;

export enum NavigationPosition {
  Start = 'start',
  End = 'end',
  Bottom = 'bottom',
  Top = 'top',
}

/**
 * Navigator type can take one of following values:
 * Mobile apps: None, DrawerNavigator, BottomTabsNavigator, Custom
 * Web apps: Standard, Custom ( since we take position seperately, so no need )
 */
export enum NavigatorType {
  None = 'None',
  DrawerNavigator = 'DrawerNavigator',
  BottomTabsNavigator = 'BottomTabsNavigator',
  Custom = 'Custom',
  Standard = 'Standard',
}

export enum NavigationSelectionVariant {
  Stroke = 'stroke',
  Solid = 'solid',
}

export enum NavigationBarType {
  Fixed = 'fixed',
  Floating = 'floating',
}

export enum NavigationItemLabelVariant {
  Expandable = 'expandable',
  Upfront = 'upfront',
  Tooltip = 'tooltip',
  None = 'none',
}

export enum NavigationItemDirection {
  Column = 'column',
  Row = 'row',
}

export enum NavigationItemAlignment {
  Start = 'start',
  Center = 'center',
}

export type NavigationItemText = {
  colors: {
    default: string;
    hover: string;
    active: string;
  };
  variant: TypographyVariants;
  weight: TypographyWeights;
  fontFamily: string;
};

export enum NavigationItemIconSize {
  XS = 'xs',
  SM = 'sm',
  MD = 'md',
  LG = 'lg',
}

export type NavigationItemIcon = {
  colors: {
    default: string;
    hover: string;
    active: string;
  };
  size: NavigationItemIconSize;
};

export type NavigationItemBackground = {
  colors: {
    default: string;
    hover: string;
    active: string;
  };
};

// XS is 52px and SM is 56px in Navbar Width
export enum ExpandableNavigationVariant {
  XS = 'xs',
  SM = 'sm',
}

export type AppearanceNavItem = {
  id: string;
  items?: AppearanceNavItem[];
  hiddenInOverflow: boolean | undefined;
  visible?: boolean;
};

export type AppearanceNavItems = AppearanceNavItem[];

type BaseNavigationBarAppearance = {
  navigatorType?: NavigatorType;
  navigatorModuleId?: string;
  overflowDetails: {
    icon: string;
    label: string;
  };
};

export type NavigationItemStyles = Pick<StyleObject, 'padding' | 'gap' | 'borderRadius'>;

export type NavigationItem = {
  selectionVariant: NavigationSelectionVariant;
  labelVariant: NavigationItemLabelVariant;
  direction?: NavigationItemDirection;
  alignment?: NavigationItemAlignment;
  alignmentColumn?: NavigationItemAlignment;
  text?: NavigationItemText;
  icon?: NavigationItemIcon;
  background?: NavigationItemBackground;
  styles?: NavigationItemStyles;
};

export type NavigationAppearance = {
  pageTransition?: Navigation['pageTransition'];
  primary: AppearanceNavItems;
  secondary?: AppearanceNavItems;
  userMenu?: AppearanceNavItems;
  navigationBar:
    | (BaseNavigationBarAppearance & {
        position: NavigationPosition.Bottom;
        type: NavigationBarType.Fixed;
      })
    | (BaseNavigationBarAppearance & {
        position: NavigationPosition.Start;
        type: NavigationBarType.Fixed | NavigationBarType.Floating;
        floatingOffset?: Pick<StyleObject, 'margin'>;
      })
    | (BaseNavigationBarAppearance & {
        position: NavigationPosition.Top;
        type: NavigationBarType.Fixed;
      })
    | (BaseNavigationBarAppearance & {
        position: NavigationPosition.End;
        type: NavigationBarType.Fixed;
      });
  navigationItem: NavigationItem;
  userReordered?: {
    primary: boolean;
    secondary: boolean;
    userMenu?: boolean;
  };
  styles?: Pick<
    StyleObject,
    | 'borderWidth'
    | 'borderRadius'
    | 'boxShadow'
    | 'height'
    | 'width'
    | 'padding'
    | 'borderColor'
    | 'gapSecondary'
  >;
  theme?: BlockThemeEnum;
};

export type Navigation = {
  // basePath for mounting client router, remove after testing if not needed
  // Deprecated: use basePath in interface instead
  basePath?: string;
  pageTransition?: 'SLIDE_HORIZONTAL' | 'FADE';
  primary: NavItems;
  secondary?: NavItems;
  defaultPageId: ValueOrCondition<string>;
  defaultPublicPageId?: ValueOrCondition<string>;
  appearance?: Partial<Record<NavigationResolution, NavigationAppearance>>;
};

// generic type for storing entity details, extensible for future properties
export type EntityDetails = {
  isPublic?: boolean;
  slug?: string;
  type: InterfacePageEntityComponentType;
};

export enum BorderVariants {
  FullySquare = 'FullySquare',
  SoftSquare = 'SoftSquare',
  Rounded = 'Rounded',
  FullyRounded = 'FullyRounded',
}

export enum LauncherPositionType {
  BOTTOM_START = 'BOTTOM_START',
  BOTTOM_END = 'BOTTOM_END',
  TOP_START = 'TOP_START',
  TOP_END = 'TOP_END',
  CUSTOM = 'CUSTOM',
}

export type LauncherPositionCoordinates = {
  bottom?: string | { custom: string };
  end?: string | { custom: string };
  top?: string | { custom: string };
  start?: string | { custom: string };
};

export type ImageFitType = 'move' | 'fill' | 'contain' | 'stretch vertical' | 'stretch horizontal';

export type SplashScreenType = {
  backgroundColor?: string;
  image?: string;
  dark?: {
    backgroundColor?: string;
    image?: string;
  };
  imageWidth?: number;
  imageHeight?: number;
  imageFit?: ImageFitType;
};

export type LauncherPosition = {
  type: LauncherPositionType;
  coordinates: LauncherPositionCoordinates;
};

export enum LauncherShape {
  CIRCLE = 'CIRCLE',
  SQUARE = 'SQUARE',
}

export type EnabledPayloadType = {
  value?: true | false | 'conditions';
  conditions?: ConditionEvaluationObject;
};

export type LegacyLauncher = {
  enabled: EnabledPayloadType;
  title: string;
  description: string;
  background: string;
  image?: string;
  iconColor?: string;
  shape: LauncherShape;
  position: LauncherPosition;
  gap: string | { custom: string };
  size: {
    width: string | { custom: string };
    height: string | { custom: string };
  };

  // optional message beside the logo
  message?: string;
  interactions?: {
    events?: Event[];
  };
};

export type Launcher = {
  enabled: EnabledPayloadType;
  title: string;
  description: string;
  /**
   * launcherType controls whether the launcher is rendered as an icon or as a image+text.
   * When not specified, it defaults to 'icon'.
   */
  launcherType?: 'icon' | 'image';
  gap: string | { custom: string };
  position: LauncherPosition;
  customCSS?: string | undefined;

  // Icon specific properties
  icon?: {
    name?: string;
    color?: string;
    width?: { custom: string };
    height?: { custom: string };
  };

  // Image specific properties
  image?: {
    url?: string;
    isUploaded?: boolean;
    height?: { custom: string };
    width?: { custom: string };
  };

  // Text properties
  text?: {
    value?: string;
    color?: TypographyColors;
    variant?: TypographyVariants;
    weight?: TypographyWeights;
  };

  // Styles
  styles?: Pick<
    StyleObject,
    | 'padding'
    | 'gap'
    | 'borderColor'
    | 'backgroundColor'
    | 'borderWidth'
    | 'borderStyle'
    | 'borderRadius'
  >;

  // optional message beside the logo
  message?: string;
  interactions?: {
    events?: Event[];
  };
};

export enum AppThemeMode {
  Light = 'light',
  Dark = 'dark',
}

type AppThemeOverrides = Record<string, string | undefined>;

export type AppThemeConfigType = AppThemeOverrides & {
  [AppThemeMode.Dark]?: Record<string, string | undefined>;
  border?: BorderVariants;
};

export enum IntegrationApps {
  CopilotChat = 'copilotChat',
  Ozonetel = 'ozonetel',
  Pusher = 'pusher',
  AppAnalytics = 'appAnalytics',

  // Mobile apps specific integrations
  MicrosoftClarity = 'microsoftClarity',
  UAEPass = 'uaePass',
  Razorpay = 'razorpay',
  CleverTap = 'cleverTap',
  YellowAI = 'yellowAI',
}

export enum InterfaceType {
  Application = 'application',
  Module = 'module',
  Package = 'package',
  TemplateComponent = 'template-component',
}

/** `properties.type` for module and template-component interfaces; pairs with `MODULE_AND_TEMPLATE_INTERFACE_TYPES` in types/page (page component kinds). */
export const MODULE_AND_TEMPLATE_RECORD_TYPES = new Set<InterfaceType>([
  InterfaceType.Module,
  InterfaceType.TemplateComponent,
]);

export const InterfaceDisplayNames: Record<InterfaceType, string> = {
  [InterfaceType.Module]: 'module',
  [InterfaceType.TemplateComponent]: 'template component',
  [InterfaceType.Application]: 'application',
  [InterfaceType.Package]: 'package',
};

export type CopilotChatIntegrationConfigType = {
  enabled: boolean;
  interfaceId: string;
  visibility?: VisibilityPayloadType;
};

export type OzonetelIntegrationConfigType = {
  enabled: boolean;
  customerId: string;
};

export type PusherIntegrationConfigType = {
  enabled: boolean;
  appKey: string;
  cluster: string;
  channels: PusherChannelConfig[];
  authEndpoint: string;
  events: PusherEventConfig[];
};

export type PusherChannelConfig = {
  channelName: string;
  label?: string;
  description?: string;
};

export type PusherEventConfig = {
  eventType: string;
  label: string;
  description?: string;
  payload?: object;
};

export type ScreenProtectionType = {
  enabled?: boolean;
  overlayImage?: string;
  backgroundColor?: string;
  dimensions?: {
    height: number;
    width: number;
  };
};

export type AppAnalyticsIntegrationType = {
  enabled: boolean;
};

export type MicrosoftClarityConfigType = {
  enabled: boolean;
  projectId: string;
  userIdentification?: {
    type: 'anonymous' | 'named';
    value?: string;
  };
  customTags?: { key: string; value: string }[];
};

export type UAEPassConfigType = {
  enabled: boolean;
  env?: 'staging' | 'production';
  clientId?: string;
};

export type RazorpayConfigType = {
  enabled: boolean;
  keyId?: string;
};

export interface CleverTapConfigType {
  enabled: boolean;
  accountId: string;
  accountToken: string;
  accountRegion?: string;
  userProperties?: { key: string; value: string }[];
}

/** Yellow.ai chat theme — bot identity fields. botName maps to `setThemeBotName`; botIcon is a remote asset URL the SDK fetches. */
export interface YellowAIThemeConfigType {
  botName?: string;
  botIcon?: string;
}

export interface YellowAIConfigType {
  enabled: boolean;
  botId: string;
  theme?: YellowAIThemeConfigType;
  // key/value pairs forwarded to the bot via YMChat.setPayload; each value is a
  // data-pill expression (e.g. "{{ userContext['username'] }}") resolved at launch.
  payload?: { key: string; value: string }[];
}

export enum AspectRatioType {
  Standard = 'STANDARD',
  UltraWide = 'ULTRA_WIDE',
  SuperUltraWide = 'SUPER_ULTRA_WIDE',
  Others = 'OTHERS',
  Custom = 'CUSTOM',
}

export enum OrientationType {
  Landscape = 'LANDSCAPE',
  Portrait = 'PORTRAIT',
}

export type DisplayDetails = {
  aspectRatioType: AspectRatioType;
  dimensions: {
    width: number;
    height: number;
  };
  orientation: OrientationType;
  grid: {
    rows: number;
    columns: number;
  };
};

export type InterfaceFolder = {
  id: string;
  name: string;
  createdTime: number;
  parentId?: string;
};

export enum PasswordRestrictionPreference {
  LowerCase = 'LOWER_CASE_PATTERN',
  UpperCase = 'UPPER_CASE_PATTERN',
  Digit = 'DIGIT_PATTERN',
  SpecialChar = 'SPECIAL_CHAR_PATTERN',
  CommonPasswords = 'COMMON_PASSWORDS_PATTERN',
  ContextSpecificPassword = 'CONTEXT_SPECIFIC_PASSWORD',
}

export const PasswordRestrictionPreferences = [
  PasswordRestrictionPreference.LowerCase,
  PasswordRestrictionPreference.UpperCase,
  PasswordRestrictionPreference.Digit,
  PasswordRestrictionPreference.SpecialChar,
  PasswordRestrictionPreference.CommonPasswords,
  PasswordRestrictionPreference.ContextSpecificPassword,
];

export type AppVariant = {
  key: string;
  label: string;
  isDefault: boolean;
};

export type InterfacePathNode = {
  id: string;
  segment: string;
  parentId: string | null;
  children?: string[];
  builderValue?: string;
  path: string;
  redirect?: {
    pageId: string | undefined;
    params?: Record<string, string>;
  } | null;
};

export enum SecurityType {
  Public = 'PUBLIC',
  Private = 'PRIVATE',
}

/**
 * per-page native screen transition animation.
 * maps to React Navigation NativeStackNavigationOptions['animation'].
 */
export enum PageTransitionBehavior {
  Default = 'default',
  None = 'none',
  Slide = 'slide_from_right',
  Fade = 'fade',
  FadeFromBottom = 'fade_from_bottom',
  Modal = 'modal',
  AdaptiveSheet = 'adaptive_sheet',
  FullScreenModal = 'full_screen_modal',
  Overlay = 'overlay',
}

export const PageTransitionGroup = {
  Standard: 'Standard',
  Modal: 'Modal',
  Expressive: 'Expressive',
} as const;
export type PageTransitionGroupType =
  (typeof PageTransitionGroup)[keyof typeof PageTransitionGroup];

export const PAGE_TRANSITION_BEHAVIOR_OPTIONS = [
  {
    const: PageTransitionBehavior.Default,
    title: 'Platform Default',
    description: 'Native default for each platform',
    group: PageTransitionGroup.Standard,
    tooltipTitle: 'Platform default',
    tooltipDescription:
      'Uses default native transitions Android fades from the right iOS slides from the right.',
    tooltipMediaPath: `${UA_ASSETS_PATH}/images/platform/transitions-default.gif`,
  },
  {
    const: PageTransitionBehavior.Slide,
    title: 'Slide',
    description: 'Slides in from the right edge',
    group: PageTransitionGroup.Standard,
    tooltipTitle: 'Slide Left',
    tooltipDescription: 'Pushes the current screen left as the new screen enters from the right.',
    tooltipMediaPath: `${UA_ASSETS_PATH}/images/platform/transitions-slide-left.gif`,
  },
  {
    const: PageTransitionBehavior.Fade,
    title: 'Fade',
    description: 'Cross-dissolve between screens',
    group: PageTransitionGroup.Standard,
    tooltipTitle: 'Cross fade',
    tooltipDescription: 'Smoothly dissolves the current screen while the next one fades into view.',
    tooltipMediaPath: `${UA_ASSETS_PATH}/images/platform/transitions-cross-fade.gif`,
  },
  {
    const: PageTransitionBehavior.None,
    title: 'None',
    description: 'Instant switch, no animation',
    group: PageTransitionGroup.Standard,
    tooltipTitle: 'Instant',
    tooltipDescription: 'Cuts directly to the next screen with zero animation or visual delay.',
    tooltipMediaPath: `${UA_ASSETS_PATH}/images/platform/transitions-instant.gif`,
  },
  {
    const: PageTransitionBehavior.AdaptiveSheet,
    title: 'Adaptive sheet',
    description: 'Card-style on iOS, sheet on Android',
    group: PageTransitionGroup.Modal,
    tooltipTitle: 'Adaptive sheet',
    tooltipDescription: 'Renders as a draggable card on iOS and a bottom sheet on Android.',
    tooltipMediaPath: `${UA_ASSETS_PATH}/images/platform/transitions-adaptive-sheet.gif`,
  },
  {
    const: PageTransitionBehavior.FullScreenModal,
    title: 'Full Screen',
    description: 'Covers entire screen from bottom',
    group: PageTransitionGroup.Modal,
    tooltipTitle: 'Full screen cover',
    tooltipDescription: 'Rises from the bottom edge and takes over the entire available screen.',
    tooltipMediaPath: `${UA_ASSETS_PATH}/images/platform/transitions-full-screen-cover.gif`,
  },
  {
    const: PageTransitionBehavior.FadeFromBottom,
    title: 'Fade from bottom',
    description: 'Fades in from the bottom',
    group: PageTransitionGroup.Expressive,
    tooltipTitle: 'Fade from bottom',
    tooltipDescription: 'Fades in from the bottom.',
    tooltipMediaPath: `${UA_ASSETS_PATH}/images/platform/transitions-rise-and-fade.gif`,
  },
  {
    const: PageTransitionBehavior.Overlay,
    title: 'Overlay',
    description: 'Overlay on the screen',
    group: PageTransitionGroup.Expressive,
    tooltipTitle: 'Overlay',
    tooltipDescription: 'Overlay on the screen.',
    tooltipMediaPath: `${UA_ASSETS_PATH}/images/platform/transitions-dimmed-overlay.gif`,
  },
] as const;

export type Interface = {
  /**
   * The type of the component,
   * helps in identifying the use case of because we use the same entity to store multiple tenant's sitemap
   */
  // componentType: 'INTERFACE';
  name: string;
  parentInterfaceId?: string;
  standard: boolean;
  metadata: Metadata;
  // backend-derived: true when a deployed e_interface_override exists for this interface.
  // read only via isInterfaceOverridden(); backend must re-derive on every deploy.
  isOverridden?: boolean;
  // set only for code-mode apps (`manifest.mode === 'code'`) — the agent-platform
  // code-builder session the app was ORIGINATED from. Provenance, stamped once at
  // creation and never moved. Do NOT route on it: it names the creator's chat, so
  // opening the builder on it walks every other user into a stranger's conversation.
  // The builder resolves the viewer's own chat instead (resolveUserLastSessionId).
  chatSessionId?: string;
  locale?: {
    defaultLocale: string; // default locale of the interface
    supportedLocales?: string[]; // other supported locales of the interface
  };
  // prefix path for all the pages in the interface
  basePath?: string;
  navigation: Navigation;
  /**
   * Variables for the interface
   */
  appVariables?: Record<string, JavaScriptVariable>;
  /**
   * Functions for the interface
   */
  appFunctions?: Record<string, JavaScriptFunction>;

  deviceDetails?: DeviceDetails;
  description?: string;
  appVariants?: {
    enabled: boolean;
    variants: AppVariant[];
  };
  manifest?: {
    type: ApplicationType;
    // how this app is authored — `code` (AI code builder) vs `config` (no-code
    // builder, the default when omitted). Orthogonal to `type`; code mode is only
    // wired for `type === WEB` today.
    mode?: BuilderModeType;
    launcher?: Launcher;
    displayDetails?: DisplayDetails;
    splashScreen?: SplashScreenType;
    brandAssets?: BrandAssetsType;
  };
  /**
   * @deprecated Use `entityDetailsMap[pageId].isPublic` instead. Will be removed later.
   * Array of public page IDs.
   */
  publicPageIds?: string[];
  /**
   * @deprecated Use `entityDetailsMap` instead. Will be removed later.
   * Maps pageId to slug.
   */
  interfacePageIdVsSlugMap?: Record<string, string | undefined>;
  paths?: Record<string, InterfacePathNode>;
  // map of entityId (pageId, moduleId, etc.) to entity details
  entityDetailsMap?: Record<string, EntityDetails>;
  theme?: {
    // stringified theme
    output?: string;
    color?: string;
    // Save theme variables in cssVars
    cssVars?: AppThemeConfigType;
    // NOTE: this is temporary, and will be updated when custom fontFamily is supported via builder @Pradeet
    fontConfig?: {
      fontFamily: string;
    };
    statusBar?: {
      style?: 'light' | 'dark' | 'auto';
    };
    navigationBar?: {
      style?: 'light' | 'dark' | 'auto';
    };
    /**
     * Default theme in which the interface should be loaded
     */
    defaultScheme?: ColorScheme;
    /**
     * Default theme ID from e_theme entity
     * Can be a simple string (single theme) or ThemeConditionalValueFilter (multiple themes with conditions)
     */
    defaultThemeId?: string | ThemeConditionalValueFilter;
    components?: ThemeComponentsType;
    customColors?: string[];
  };
  pagePermissions?: PagePermissions;
  /**
   * per-page native screen transition animation, keyed by pageId
   */
  pageTransitionBehavior?: Record<string, PageTransitionBehavior>;
  customCode?: {
    header?: string;
    footer?: string;
    htmlAttributes?: { key: string; value: string }[];
  };
  security?: SecurityProperties & {
    type: 'PUBLIC' | 'PRIVATE';
    native?: {
      rootedDevicePrevention?: { enabled?: boolean };
      certificatePinning?: {
        enabled?: boolean;
        domains?: {
          domain: string;
          publicKeyHash: string;
        }[];
      };
      screenProtection?: ScreenProtectionType;
    };
    passwordRestrictions?: {
      lengthLimits?: {
        minLength?: number;
        maxLength?: number;
      };
      preferences?: PasswordRestrictionPreference[];
      /** Number of days after which users must change their password */
      expiryDays?: number;
      /** Minimum number of days a user must wait before changing their password */
      minAgeDays?: number;
      /** Number of previous passwords the system will remember and block */
      historySize?: number;
    };
  };
  flags?: {
    shouldReEvaluateUsingEntityIds?: boolean;
    /**
     * Stops nested re-evaluation entirely, but only during the node SSR pass.
     * Stronger than shouldReEvaluateUsingEntityIds and takes precedence over it;
     * the browser is unaffected and still resolves nested expressions.
     */
    shouldSkipReEvaluationOnServer?: boolean;
    showBreadcrumbsInEmbeddedApp?: boolean;
    /**
     * Enable Automation Templates section in the UI
     */
    enableAutomationTemplates?: boolean;
  };
  offlineMode?: {
    enabled: boolean;
    syncEntities?: {
      entityType?: string;
      config: {
        filter?: ResolvedUIFilter;
        page?: Page;
        search?: SearchEntitiesDTO;
      };
    }[];
  };
  integrations?: {
    [IntegrationApps.CopilotChat]?: CopilotChatIntegrationConfigType;
    [IntegrationApps.Ozonetel]?: OzonetelIntegrationConfigType;
    [IntegrationApps.Pusher]?: PusherIntegrationConfigType;
    [IntegrationApps.AppAnalytics]?: AppAnalyticsIntegrationType;

    // Mobile apps specific integrations
    [IntegrationApps.MicrosoftClarity]?: MicrosoftClarityConfigType;
    [IntegrationApps.UAEPass]?: UAEPassConfigType;
    [IntegrationApps.Razorpay]?: RazorpayConfigType;
    [IntegrationApps.CleverTap]?: CleverTapConfigType;
    [IntegrationApps.YellowAI]?: YellowAIConfigType;
  };
  type?: InterfaceType;
  /**
   * Marks this interface as a template host. The interface is cosmetic — it only
   * exists so the builder has a real interface to edit (normal collab/save/deploy).
   * The queryable template data lives on the single child `e_component`.
   */
  isTemplate?: boolean;
  package?: {
    security?: SecurityProperties & {
      type: 'PUBLIC' | 'PRIVATE';
    };
  };
  notifications?: {
    tags?: {
      id: string;
      label: string;
      hidden: boolean;
    }[];
    pushNotifications?: {
      enabled?: boolean;
      autoRegister?: boolean; // if true, the push notifications will be registered automatically when the app is launched
      connection: {
        connectionId?: string;
        firebaseProjectId?: string;
        androidServiceFile?: string;
        iosServiceFile?: string;
      };
    };
  };
  disableMqtt?: boolean;
  mqttTopics?: MQTTTopicType[];
  realtimeEvents?: {
    enabled?: boolean;
  };
  customEvents?: CustomEventType[];
  embeds?: {
    container?: {
      styles?: React.CSSProperties;
    };
  } & Record<
    App,
    {
      container?: {
        styles?: React.CSSProperties;
      };
    }
  >;
};

export type InterfaceEntity = Entity<Interface>;
